import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_STORAGE_PROTECTION_EXCLUSIONS_GRID } from '../../../../../../../webcomponents.constants';
import { StorageExclusionsSearchableKeywords } from '../../../../utils/uiSettings/storageProtection/storage-protection.types';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import { Column, ColumnKeys, getGridColumnDetails, GRID_COLUMNS } from './grid-config';
import { GzStorageProtectionExclusionsGridDataProvider } from './GzStorageProtectionExclusionsGridDataProvider';
import { html, TemplateResult } from 'lit';
import { ValidationResult } from '../../../../../../../ui/gzForm/formField/types/form-validator';
import { validateDuplicates, validateHashesIfNeeded, validateType } from '../utils/validators';
import {
  convertExclusionItemFormValue,
  sanitizeExclusionItem,
  sanitizeExclusionItemFormValue,
} from '../utils/converters';
import { GRID_ITEM_FORM_ID } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/base/component/GzPolicyUiSettingsBaseGrid';
import { translateFn } from '../../../../../../../utils/translator.helper';
import {
  ImportFileApiClients,
  PolicyDependenciesContainer,
  StorageProtectionApiClients,
} from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { GzPolicyUiSettingsImportDialog } from '../../../../../../common/policyPanel/uiSettings/dialogs/import/GzPolicyUiSettingsImportDialog';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import { CustomizableValidator } from '../../../../../../../ui/gzForm/validators/customizable.validator';
import { GridCellEditWebComponent } from '../../../../../../../grid/uiElements/gridCellEdit';
import { GzButtonWebComponent } from '../../../../../../../ui/gzButton';
import { selectPoliciesState } from '../../../../redux/slice';
import { RootState, store } from '@nirvana/redux/store';
import { getPhantom } from '../../../../redux/actions';
import { TextHighlightMixin } from '../../../../../../../mixins';
import { connect } from 'pwa-helpers';
import { getStorageExclusionsGridSearchableKeywords } from '../../../../utils/uiSettings/storageProtection/storage-protection.values';
import { selectAccordionsSearchState } from '@nirvana/redux/searchable-ui-accordions';
import {
  StorageExclusionItemType,
  StorageProtectionExclusionItem,
  StorageProtectionExclusionItemFormValue,
} from '@nirvana/shared/dtos/types/policies/common';
import { ButtonTypes } from '../../../../../../../ui/gzButton/types';
import { toggleVisibility } from '../../../../../../../utils/htmlElement.utils';

class GzStorageProtectionExclusionsGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  StorageProtectionExclusionItem,
  StorageProtectionExclusionItemFormValue,
  GzStorageProtectionExclusionsGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected clients = PolicyDependenciesContainer.getInstance().resolveDependency(
    'StorageProtection'
  ) as StorageProtectionApiClients;
  protected searchableKeywords = getStorageExclusionsGridSearchableKeywords();
  protected translations = {
    editRow: translateFn('Edit exclusion'),
    deleteRow: translateFn('Delete'),
  };

  @query('#export-csv-button')
  protected exportCsvButton: GzButtonWebComponent;

  protected getDataProvider(): GzStorageProtectionExclusionsGridDataProvider {
    return new GzStorageProtectionExclusionsGridDataProvider(this.initialGridItems);
  }

  protected renderGridItemForm(): TemplateResult {
    return html` <gz-policy-panel-storage-protection-exclusion-item
      id="${GRID_ITEM_FORM_ID}"
    ></gz-policy-panel-storage-protection-exclusion-item>`;
  }

  protected renderHeaderButtonsOutsideGrid(): TemplateResult {
    return html`
      <gz-tooltip
        id="tooltip"
        class="tooltip"
        tooltipText="${translateFn('Export exclusions (available only for saved policies)')}"
      >
        <gz-button
          searchable-label="${this.searchableKeywords[StorageExclusionsSearchableKeywords.StorageExport].label}"
          id="export-csv-button"
          type="${ButtonTypes.OUTLINE}"
          @click="${this.exportExclusions}"
          >${translateFn('Export')}</gz-button
        >
      </gz-tooltip>
      <gz-button
        searchable-label="${this.searchableKeywords[StorageExclusionsSearchableKeywords.StorageImport].label}"
        id="import-csv-button"
        type="${ButtonTypes.OUTLINE}"
        @click="${this.importExclusions}"
        >${translateFn('Import')}</gz-button
      >
    `;
  }

  protected onGridItemsChange() {
    super.onGridItemsChange();
    const state = selectPoliciesState(store.getState());
    const phantom = getPhantom(state);

    // disable export button if policy is not saved
    if (phantom) {
      this.exportCsvButton.disabled = phantom;
      return;
    }

    // disable export button if there are no items
    this.exportCsvButton.disabled = !(this.gridComponent.dataProvider as GzStorageProtectionExclusionsGridDataProvider)
      .items.length;
    toggleVisibility(
      this.gridComponent,
      !!(this.gridComponent.dataProvider as GzStorageProtectionExclusionsGridDataProvider).items.length
    );
  }

  protected sanitizeGridItemBeforeAdd(
    gridItemFormValue: StorageProtectionExclusionItemFormValue
  ): StorageProtectionExclusionItemFormValue {
    return sanitizeExclusionItemFormValue(gridItemFormValue);
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: StorageProtectionExclusionItemFormValue): ValidationResult {
    // check for duplicates
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzStorageProtectionExclusionsGridDataProvider).items
    );
  }

  protected convertGridItemBeforeAdd(
    gridItemFormValue: StorageProtectionExclusionItemFormValue
  ): StorageProtectionExclusionItem {
    return convertExclusionItemFormValue(gridItemFormValue);
  }

  protected onEditRowEnabled(event: CustomEvent) {
    // set validators on edited row
    const { rowData } = event.detail;

    this.gridComponent.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.PATH, [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator(value => validateHashesIfNeeded(rowData.item.type as StorageExclusionItemType, value)),
    ]);
  }

  protected isEditedRowChanged(
    oldItem: StorageProtectionExclusionItem,
    newItem: StorageProtectionExclusionItem
  ): boolean {
    return oldItem.path !== newItem.path || oldItem.description !== newItem.description;
  }

  protected validateGridItemBeforeEdit(
    oldItem: StorageProtectionExclusionItem,
    newItem: StorageProtectionExclusionItem
  ): ValidationResult {
    // skip if path is not changed
    if (newItem.path === oldItem.path) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected sanitizeGridItemBeforeEdit(gridItem: StorageProtectionExclusionItem): StorageProtectionExclusionItem {
    return sanitizeExclusionItem(gridItem);
  }

  protected revertGridCellEditValue(
    gridCellEdit: GridCellEditWebComponent,
    oldItem: StorageProtectionExclusionItem
  ): void {
    if (gridCellEdit.id === ColumnKeys.PATH) {
      gridCellEdit.value = oldItem.path;
      return;
    }

    if (gridCellEdit.id === ColumnKeys.DESCRIPTION) {
      gridCellEdit.value = oldItem.description;
      return;
    }
  }

  private exportExclusions() {
    this.clients.ExportStorageExclusions(this.policyId);
  }

  private importExclusions() {
    const importFn = async file => {
      const csvFields = '{"type":"int","path":"string","description":"string"}';
      const importClient = PolicyDependenciesContainer.getInstance().resolveDependency(
        'ImportFile'
      ) as ImportFileApiClients;

      return await importClient.ImportCsv(csvFields, file);
    };

    const dialog = new GzPolicyUiSettingsImportDialog(importFn, this.appendExclusionsItemsToGrid.bind(this));
    dialog.show();
  }

  private appendExclusionsItemsToGrid(exclusionItems: StorageProtectionExclusionItemFormValue[]): {
    valid: boolean;
    rowWithErrors: number[];
  } {
    this.gridComponent.propertiesSet = false;

    const exclusionsInGrid = (this.gridComponent.dataProvider as GzStorageProtectionExclusionsGridDataProvider).items;

    const exclusionItemsToAppendLength = exclusionItems.length;
    const rowWithErrors: number[] = [];
    let exclusionItem: StorageProtectionExclusionItemFormValue;

    for (let i = 0; i < exclusionItemsToAppendLength; i++) {
      exclusionItem = exclusionItems[i];

      // validate empty fields
      if (!exclusionItem || !exclusionItem.path || !exclusionItem.type) {
        rowWithErrors.push(i + 1);
        continue;
      }

      // validate type
      if (!validateType(exclusionItem.type).valid) {
        rowWithErrors.push(i + 1);
        continue;
      }

      // sanitize exclusion item
      exclusionItem = sanitizeExclusionItemFormValue(exclusionItem);

      // validate hash
      if (!validateHashesIfNeeded(exclusionItem.type, exclusionItem.path).valid) {
        rowWithErrors.push(i + 1);
        continue;
      }

      // check for duplicates - error is skipped
      if (!validateDuplicates(exclusionItem, exclusionsInGrid).valid) {
        continue;
      }

      (this.gridComponent.dataProvider as GzStorageProtectionExclusionsGridDataProvider).addItem(
        convertExclusionItemFormValue(exclusionItem)
      );
    }
    this.gridComponent.propertiesSet = true;
    this.onGridItemsChange();

    return {
      valid: !rowWithErrors.length,
      rowWithErrors: rowWithErrors,
    };
  }
}

/**
 * This wrapper is necessary to connect the web component to the store.
 */
@customElement(WEB_COMPONENT_POLICY_PANEL_STORAGE_PROTECTION_EXCLUSIONS_GRID)
export class GzStorageProtectionExclusionsGridWrapperWebComponent extends TextHighlightMixin(
  connect(store)(GzStorageProtectionExclusionsGridWebComponent)
) {
  /**
   * State changed, update search string.
   * @param rootState
   */
  public stateChanged(rootState: RootState) {
    const accordionsSearchApp = selectAccordionsSearchState(rootState);
    this.searchString = accordionsSearchApp.searchString;
  }
}
